import type { StreamingChatClient, ChatStatus } from './types'
import { apiRoutes } from './botApi'
import { fetchSseApi } from './http'

export class SseStreamingChatClient implements StreamingChatClient {
  private status: ChatStatus = 'idle'
  private messageHandler: ((data: unknown) => void) | null = null
  private errorHandler: ((err: unknown) => void) | null = null
  private statusHandler: ((status: ChatStatus) => void) | null = null
  private abortController: AbortController | null = null

  constructor(_baseUrl = '') {}

  async connect(): Promise<void> {
    this.setStatus('connecting')
    this.setStatus('connected')
  }

  disconnect(): void {
    this.abortController?.abort()
    this.abortController = null
    this.setStatus('disconnected')
  }

  send(message: string | object): void {
    if (this.status !== 'connected') {
      this.errorHandler?.(new Error('Not connected'))
      return
    }

    let content: string
    let fileIds: string[] = []
    let botId: string | undefined
    let sessionId: string | null = null
    let replaceLastTurn = false

    if (typeof message === 'string') {
      content = message
    } else {
      const msg = message as {
        kind?: string
        content?: string
        fileIds?: string[]
        botId?: string
        sessionId?: string | null
        replaceLastTurn?: boolean
        correlationId?: string
        actionId?: string
        payload?: Record<string, unknown>
      }
      const kind = msg.kind ?? 'text'
      content = msg.content ?? ''
      fileIds = msg.fileIds ?? []
      botId = msg.botId
      sessionId = msg.sessionId ?? null
      replaceLastTurn = msg.replaceLastTurn === true
      if (kind === 'structured') {
        if (!botId || !sessionId || !msg.correlationId || !msg.actionId) {
          this.errorHandler?.(new Error('Invalid structured submission payload'))
          return
        }
        this.abortController = new AbortController()
        this.streamStructuredRequest(
          botId,
          sessionId,
          msg.correlationId,
          msg.actionId,
          msg.payload ?? {},
          this.abortController.signal,
        )
        return
      }
      console.log('[SseStreamingChatClient] send() extracted botId:', botId, 'sessionId:', sessionId)
    }

    this.abortController = new AbortController()
    this.streamRequest(
      content,
      fileIds,
      botId,
      sessionId,
      replaceLastTurn,
      this.abortController.signal,
    )
  }

  private async streamStructuredRequest(
    botId: string,
    sessionId: string,
    correlationId: string,
    actionId: string,
    payload: Record<string, unknown>,
    signal: AbortSignal,
  ): Promise<void> {
    const body: Record<string, unknown> = {
      sessionId,
      correlationId,
      actionId,
      payload,
    }
    await this.streamRawRequest(
      apiRoutes.bots.submissions.path(botId),
      body,
      signal,
    )
  }

  private async streamRequest(
    content: string,
    fileIds: string[],
    botId: string | undefined,
    sessionId: string | null,
    replaceLastTurn: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    if (!botId) {
      this.errorHandler?.(new Error('botId is required'))
      return
    }
    const body: Record<string, unknown> = { sessionId, content, fileIds, replaceLastTurn }
    await this.streamRawRequest(
      apiRoutes.bots.messages.path(botId),
      body,
      signal,
    )
  }

  private async streamRawRequest(path: string, body: Record<string, unknown>, signal: AbortSignal): Promise<void> {
    try {
      const response = await fetchSseApi(path, {
        method: apiRoutes.bots.messages.method,
        promptForAuthentication: true,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      })

      if (!response.ok) {
        throw await buildStreamingHttpError(response)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let buffer = ''

      let eventType = ''
      
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            const data = line.slice(5).trim()
            if (eventType && data) {
              this.handleEvent(eventType, data)
            }
            eventType = ''
          }
        }
      }

      // Process any remaining data in buffer after stream ends
      if (buffer.trim()) {
        const lines = buffer.split('\n')
        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventType = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            const data = line.slice(5).trim()
            if (eventType && data) {
              this.handleEvent(eventType, data)
            }
            eventType = ''
          }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        this.errorHandler?.(err)
      }
    }
  }

  private handleEvent(eventType: string, data: string): void {
    if (eventType === 'ping') return

    try {
      const parsed = JSON.parse(data)

      switch (eventType) {
        case 'status':
          this.messageHandler?.({ type: 'status', message: parsed.message })
          break
        case 'chunk':
          this.messageHandler?.({ type: 'chunk', content: parsed.content, done: false })
          break
        case 'sources_used':
          this.messageHandler?.({ type: 'sources_used', sources: parsed.sources })
          break
        case 'artifact':
          this.messageHandler?.({ 
            type: 'artifact', 
            artifactType: parsed.artifactType, 
            placement: parsed.placement === 'before_text' || parsed.placement === 'after_text' ? parsed.placement : undefined,
            messageRole: parsed.messageRole === 'user' ? 'user' : 'assistant',
            messageId: typeof parsed.messageId === 'string' ? parsed.messageId : undefined,
            source: parsed.source === 'user' ? 'user' : parsed.source === 'agent' ? 'agent' : undefined,
            toolName: typeof parsed.toolName === 'string' ? parsed.toolName : undefined,
            toolCallId: typeof parsed.tool_call_id === 'string' ? parsed.tool_call_id : undefined,
            data: parsed.data 
          })
          break
        case 'tool_status': {
          this.messageHandler?.({
            type: 'tool_status',
            toolName: parsed.toolName,
            status: parsed.status,
            toolCallId: typeof parsed.tool_call_id === 'string' ? parsed.tool_call_id : undefined,
            message: typeof parsed.message === 'string' ? parsed.message : undefined,
          })
          break
        }
        case 'session_updated':
          this.messageHandler?.({ type: 'session_updated', sessionId: parsed.sessionId, title: parsed.title })
          break
        case 'message_created':
          this.messageHandler?.({
            type: 'message_created',
            userMessageId: parsed.userMessageId,
            assistantMessageId: parsed.assistantMessageId,
          })
          break
        case 'done':
          this.messageHandler?.({
            type: 'done',
            done: true,
            status: typeof parsed.status === 'string' ? parsed.status : undefined,
            message: typeof parsed.message === 'string' ? parsed.message : undefined,
            sources: parsed.sources,
          })
          break
        case 'error':
          this.errorHandler?.({
            message: parsed.message || parsed.code || 'Failed to get response.',
            code: typeof parsed.code === 'string' ? parsed.code : undefined,
            status: typeof parsed.status === 'number' ? parsed.status : undefined,
            templateId: parsed.templateId,
            templateData: parsed.templateData,
          })
          break
      }
    } catch {
      // Ignore parse errors for malformed SSE data
    }
  }

  onMessage(handler: (data: unknown) => void): void {
    this.messageHandler = handler
  }

  onError(handler: (err: unknown) => void): void {
    this.errorHandler = handler
  }

  onStatusChange(handler: (status: ChatStatus) => void): void {
    this.statusHandler = handler
  }

  private setStatus(status: ChatStatus): void {
    this.status = status
    this.statusHandler?.(status)
  }
}

async function buildStreamingHttpError(response: Response): Promise<Error & {
  code?: string
  status?: number
  templateData?: Record<string, unknown>
}> {
  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    payload = undefined
  }
  const data = payload && typeof payload === 'object' ? payload as Record<string, unknown> : {}
  const message = typeof data.message === 'string'
    ? data.message
    : typeof data.error === 'string'
      ? data.error
      : `HTTP ${response.status}`
  const error = new Error(message) as Error & {
    code?: string
    status?: number
    templateData?: Record<string, unknown>
  }
  error.status = response.status
  if (typeof data.code === 'string') {
    error.code = data.code
  }
  error.templateData = {
    status: response.status,
    code: error.code ?? '',
  }
  return error
}
